import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IconName } from '@skylab-assets/icons';

export interface IconConfig {
	lazy?: boolean;
	basePath?: string;
}

export const ICON_CONFIG = new InjectionToken<IconConfig>('ICON_CONFIG');

export function provideIconConfig(config: IconConfig | boolean): Provider {
	const normalizedConfig: IconConfig = typeof config === 'boolean' ? { lazy: config } : config;
	return {
		provide: ICON_CONFIG,
		useValue: normalizedConfig,
	};
}

const DEFAULT_ICONS_PATH = '/assets/icons' as const;

@Injectable({
	providedIn: 'root',
})
export class IconRegistryService {
	readonly #sanitizer = inject(DomSanitizer);
	readonly #config = inject(ICON_CONFIG, { optional: true });

	readonly #svgCache = new Map<string, string>();
	readonly #svgRequests = new Map<string, Promise<string>>();
	readonly #safeHtmlCache = new Map<string, SafeHtml>();

	public getIconUrl(name: IconName): string {
		const basePath = this.#config?.basePath ?? DEFAULT_ICONS_PATH;
		const normalizedPath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
		return `${normalizedPath}/icon-${name}.svg`;
	}

	public async getIcon(name: IconName): Promise<SafeHtml | undefined> {
		const url = this.getIconUrl(name);
		const cachedSafe = this.#safeHtmlCache.get(url);
		if (cachedSafe) return cachedSafe;

		const raw = await this.#fetchIcon(url);
		if (!raw) return undefined;

		const safeHtml = this.#sanitizer.bypassSecurityTrustHtml(raw);
		this.#safeHtmlCache.set(url, safeHtml);
		return safeHtml;
	}

	async #fetchIcon(url: string): Promise<string> {
		const cached = this.#svgCache.get(url);
		if (cached !== undefined) return cached;

		const pending = this.#svgRequests.get(url);
		if (pending) return pending;

		const request = this.#loadIcon(url);
		this.#svgRequests.set(url, request);
		return request;
	}

	async #loadIcon(url: string): Promise<string> {
		try {
			const res = await fetch(url);
			if (!res.ok) return '';

			const text = await res.text();
			this.#svgCache.set(url, text);
			return text;
		} catch {
			return '';
		} finally {
			this.#svgRequests.delete(url);
		}
	}
}
